'use client'

import { useEffect, useState } from 'react'

/** Fallback delay when the browser has no `requestIdleCallback` (Safari). */
const FALLBACK_DELAY_MS = 200

/** Upper bound on how long an idle deferral may wait for a quiet frame. */
const IDLE_TIMEOUT_MS = 2000

/**
 * Returns false on the first render, then true once the browser is idle.
 *
 * For work that must happen soon after mount but has no business competing
 * with the first paint: opening a WebSocket, firing a query whose result is
 * empty at t=0. Callers gate on the return value, so the deferred work runs in
 * a later commit rather than in the mount commit.
 *
 * `initiallyReady` skips the deferral entirely for the case where the work is
 * already needed (a remount mid-call, say). Read it from a store snapshot, not
 * a subscription: it is only consulted on the first render.
 *
 * Falls back to a short timeout where `requestIdleCallback` is unavailable,
 * and cancels whichever it scheduled on unmount.
 */
export function useIdleReady(initiallyReady = false): boolean {
  const [ready, setReady] = useState(initiallyReady)

  useEffect(() => {
    if (ready) return

    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(() => setReady(true), {
        timeout: IDLE_TIMEOUT_MS,
      })
      return () => window.cancelIdleCallback(handle)
    }

    const timer = window.setTimeout(() => setReady(true), FALLBACK_DELAY_MS)
    return () => window.clearTimeout(timer)
    // `ready` only ever goes false → true, and the effect no-ops once true.
  }, [ready])

  return ready
}
